import { inject, Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, map, Observable, switchMap, take, tap } from 'rxjs';
import { UserRole } from '../models/user.model';
import * as ProfileActions from '../stores/profile/profile.actions';
import { selectProfileState } from '../stores/profile/profile.selector';
import { LoadingState } from '../stores/store.states';

/** The roles the backend serves report queries to, i.e. hasAnyRole('Administrator','Maintainer'). */
export const REPORT_QUERY_ROLES: readonly UserRole[] = [
  UserRole.ADMIN,
  UserRole.MAINTAINER,
];

/**
 * The dashboard is built entirely from report queries, which the backend rejects with 403 for
 * anyone but an administrator or a maintainer. Without this guard a pilot reaches a page that can
 * only ever render errors.
 */
@Injectable({ providedIn: 'root' })
export class ReportAccessGuard implements CanActivate {
  private readonly store = inject(Store);
  private readonly router = inject(Router);

  canActivate(): Observable<boolean | UrlTree> {
    return this.store.select(selectProfileState).pipe(
      take(1),
      tap((profile) => {
        if (profile.quantumProfileLoadingState === LoadingState.FUTURE) {
          this.store.dispatch(ProfileActions.loadingProfile());
        }
      }),
      switchMap(() => this.store.select(selectProfileState)),
      filter(
        (profile) =>
          profile.quantumProfileLoadingState === LoadingState.SUCCESS ||
          profile.quantumProfileLoadingState === LoadingState.ERROR,
      ),
      take(1),
      map((profile) =>
        REPORT_QUERY_ROLES.includes(profile.role)
          ? true
          : this.router.createUrlTree(['/']),
      ),
    );
  }
}
